import invariant from 'tiny-invariant'
import { SpanStatusCode, trace } from '@opentelemetry/api'
import { localStore } from 'utils/storage'

import { resolveGraphQLUrl } from '@repo/env-graphql-url'

import { createSettledIdentityGate } from './settled-identity'
import { persistUserAuthTokenFromResponse } from './user-auth-token-persister'
import type { FetchOptions, GraphQLErrorExtensions, GraphQLErrorItem } from './fetcher.types'
import {
  getOperationName,
  resolveOperationUrl,
  serializeGraphQLRequest,
  getCookie,
  AUTH_USER_ID_COOKIE_NAME,
  TOKEN_LOCAL_STORAGE_KEY,
} from './fetcher.utils'

export const GRAPHQL_URL = resolveGraphQLUrl()
export const INTERNAL_GRAPHQL_URL = process.env.INTERNAL_GRAPHQL_URL || GRAPHQL_URL

const LOCAL_AUTH_TOKEN =
  process.env.NEXT_PUBLIC_LOCAL_AUTH_TOKEN || process.env.VITE_LOCAL_AUTH_TOKEN
const WP_COOKIE_NAME = process.env.NEXT_PUBLIC_WP_COOKIE_NAME || process.env.VITE_WP_COOKIE_NAME

invariant(GRAPHQL_URL, 'GRAPHQL_URL must be set inside packages/codegen/fetcher.ts')
invariant(WP_COOKIE_NAME, 'WP_COOKIE_NAME must be set inside packages/codegen/fetcher.ts')

const tracer = trace.getTracer('default')

export function resolveToken() {
  try {
    const tokenFromStorage = localStore.getString(TOKEN_LOCAL_STORAGE_KEY)

    if (tokenFromStorage) {
      return tokenFromStorage
    }
  } catch {}

  if (LOCAL_AUTH_TOKEN) {
    return LOCAL_AUTH_TOKEN
  }

  return null
}

export function resolveCookie() {
  return getCookie(WP_COOKIE_NAME!)
}

const sendWithSettledIdentity = createSettledIdentityGate(
  () =>
    typeof document === 'undefined' ||
    Boolean(resolveToken()) ||
    Boolean(getCookie(AUTH_USER_ID_COOKIE_NAME)),
)

async function persistUserAuthTokenIfPresent(query: string, response: Response) {
  if (getOperationName(query) !== 'userAuth') {
    return
  }

  try {
    const json = (await response.clone().json()) as { data?: { me?: { token?: string | null } } }
    await persistUserAuthTokenFromResponse(json.data?.me?.token ?? undefined)
  } catch {}
}

const limit = process.env.NEXT_PUBLIC_FETCHER_LIMIT
  ? // oxlint-disable-next-line radix
    parseInt(process.env.NEXT_PUBLIC_FETCHER_LIMIT)
  : 10_000

async function graphql<Data, Variables>(
  query: string,
  variables?: Variables,
  initialHeaders?: HeadersInit,
  fetchOptions?: RequestInit,
  gql: 'default' | 'internal' = 'default',
  allowPartialData = false,
) {
  const now = Date.now()

  const headers = new Headers(initialHeaders)
  const request = serializeGraphQLRequest(query, variables)

  headers.append('accept', 'application/json, multipart/mixed')
  if (request.isMultipart) {
    headers.delete('content-type')
  } else {
    headers.set('content-type', 'application/json')
  }

  const baseUrl = gql === 'default' ? GRAPHQL_URL! : INTERNAL_GRAPHQL_URL!

  const res = await sendWithSettledIdentity(async () => {
    const token = resolveToken()
    if (token) {
      headers.append('Authorization', `Bearer ${token}`)
    }

    const response = await fetch(resolveOperationUrl(query, baseUrl), {
      ...fetchOptions,
      method: 'POST',
      credentials: 'include',
      headers,
      body: request.body,
    })

    await persistUserAuthTokenIfPresent(query, response)

    return response
  })

  const duration = Date.now() - now
  if (duration > limit) {
    // eslint-disable-next-line no-console
    // console.warn(
    //   `GraphQL fetcher: ${duration}ms`,
    //   gql === 'default' ? GRAPHQL_URL! : INTERNAL_GRAPHQL_URL!,
    //   cleanQuery,
    // )
  }
  const json = await res.json()

  handleErrors(json.errors, allowPartialData && json.data !== null && json.data !== undefined)

  return json.data as Data
}

export class GraphQLClientError extends Error {
  readonly path: ReadonlyArray<string | number> | undefined
  readonly extensions: GraphQLErrorExtensions

  constructor(
    message: string,
    options?: { extensions?: GraphQLErrorExtensions; path?: ReadonlyArray<string | number> },
  ) {
    super(message)
    this.name = 'GraphQLClientError'
    this.extensions = options?.extensions ?? {}
    this.path = options?.path
  }
}

function isGraphQLErrorItem(value: unknown): value is GraphQLErrorItem {
  return (
    typeof value === 'object' &&
    value !== null &&
    'message' in value &&
    typeof (value as { message: unknown }).message === 'string'
  )
}

function normalizeGraphQLErrors(errors: unknown): GraphQLErrorItem[] {
  if (!Array.isArray(errors)) {
    return []
  }
  return errors.filter(isGraphQLErrorItem)
}

function handleErrors(errors: unknown, canReturnPartialData = false) {
  const items = normalizeGraphQLErrors(errors)
  if (items.length === 0) {
    return
  }

  const captured = new Set<string>()

  for (const error of items) {
    if (captured.has(error.message)) {
      continue
    }
    captured.add(error.message)

    // eslint-disable-next-line no-console
    console.error(error.message, error.extensions ?? {})
  }

  if (canReturnPartialData) {
    return
  }

  const first = items[0]
  if (first) {
    throw new GraphQLClientError(first.message, {
      extensions: first.extensions ?? {},
      path: first.path,
    })
  }
}

export function fetcher<Data, Variables>(
  query: string,
  variables?: Variables,
  headers?: HeadersInit,
  {
    allowPartialData = false,
    sensitive = true,
    gql = 'default',
    ...fetchOptions
  }: FetchOptions = {},
) {
  // oxlint-disable-next-line require-await
  return async function fetcherInner() {
    // oxlint-disable-next-line require-await
    return tracer.startActiveSpan('graphql', async span => {
      span.setAttribute('sensitive', String(sensitive))
      span.setAttribute('graphql.url', resolveOperationUrl(query, GRAPHQL_URL || 'undefined'))

      if (!sensitive) {
        span.setAttribute('query', getCleanQuery(query))
        span.setAttribute('variables', stringify(variables))
        span.setAttribute('fetchOptions', stringify(fetchOptions))
      }

      return (
        graphql<Data, Variables>(query, variables, headers, fetchOptions, gql, allowPartialData)
          .then(res => {
            span.setStatus({ code: SpanStatusCode.OK })
            return res
          })
          // oxlint-disable-next-line unicorn/catch-error-name
          .catch(err => {
            span.setStatus({
              code: SpanStatusCode.ERROR,
              message: err instanceof Error ? err.message : String(err),
            })
            throw err
          })
          .finally(() => {
            span.end()
          })
      )
    })
  }
}

function stringify(options: unknown) {
  try {
    return JSON.stringify(options)
  } catch {
    return 'Failed to parse'
  }
}

function getCleanQuery(query: string) {
  // remove any \n or remove multiple white spaces
  return query.replace(/\n/g, '').replace(/\s+/g, ' ')
}
